import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import GeneralTablesCellsTdLinkTo from 'lumen/components/general/tables/cells/td-link-to';
import MarketCustomReportingTableCellsTdReportActions from 'lumen/components/market/custom-reporting/table/cells/td-report-actions';
import type { LookerScheduledPlan } from 'lumen/models';
import type MarketCustomReportingScheduledReportsRoute from 'lumen/routes/market/custom-reporting/scheduled-reports';
import { cronUserFriendly } from 'lumen/utils/cron';
import type { ModelFrom } from 'lumen/utils/routes';

const COLUMNS = {
  NAME: 'name',
  SCHEDULED: 'scheduled',
  LAST_RUN_AT: 'last-run-at',
  TYPE: 'type',
  RECIPIENTS: 'recipients',
  REPORT: 'report',
} as const;

type SortableField = 'name' | 'type' | 'last-run-at';
const DEFAULT_SORT = COLUMNS.LAST_RUN_AT;
const DEFAULT_DIRECTION = 'desc';

export default class MarketCustomReportingScheduledReportsController extends Controller {
  declare model: ModelFrom<MarketCustomReportingScheduledReportsRoute>;

  queryParams = ['page', 'limit', 'sort', 'direction'];

  // eslint-disable-next-line ember/no-unused-services
  @service declare access: Services['access'];
  @service declare customReporting: Services['custom-reporting'];
  @service declare intl: Services['intl'];

  @tracked direction: 'desc' | 'asc' = DEFAULT_DIRECTION;
  @tracked limit = 20;
  @tracked page = 1;
  @tracked sort: SortableField = DEFAULT_SORT;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    return [
      {
        name: this.intl.t('custom_reporting.schedule_report.table.name'),
        textAlign: 'left' as const,
        valuePath: COLUMNS.NAME,
        tdComponent: GeneralTablesCellsTdLinkTo,
      },
      {
        name: this.intl.t('custom_reporting.schedule_report.table.scheduled'),
        textAlign: 'left' as const,
        valuePath: COLUMNS.SCHEDULED,
        isSortable: false,
        minWidth: 190,
        width: 190,
        maxWidth: 190,
      },
      {
        name: this.intl.t('custom_reporting.schedule_report.table.last_sent'),
        textAlign: 'left' as const,
        valuePath: COLUMNS.LAST_RUN_AT,
        isSortable: true,
        minWidth: 176,
        width: 176,
        maxWidth: 176,
      },
      {
        name: this.intl.t('custom_reporting.schedule_report.table.type'),
        textAlign: 'left' as const,
        valuePath: COLUMNS.TYPE,
        minWidth: 100,
        width: 100,
        maxWidth: 100,
        isSortable: true,
      },
      {
        name: this.intl.t('custom_reporting.schedule_report.table.recipients'),
        textAlign: 'left' as const,
        valuePath: COLUMNS.RECIPIENTS,
        minWidth: 80,
        width: 80,
        maxWidth: 80,
        isSortable: false,
      },
      {
        name: '',
        textAlign: 'left' as const,
        valuePath: COLUMNS.REPORT,
        tdComponent: MarketCustomReportingTableCellsTdReportActions,
        minWidth: 68,
        width: 68,
        maxWidth: 68,
        isSortable: false,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get reports() {
    return this.model.reports.lastSuccessful?.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    if (!this.reports) {
      return [];
    }

    // Looker API does not support pagination, so we need to slice/sort the array.
    const startIndex = (this.page - 1) * this.limit;
    const endIndex = this.page * this.limit;

    return (
      [...this.reports]
        // eslint-disable-next-line @typescript-eslint/unbound-method
        .sort(this.sortReports)
        .slice(startIndex, endIndex)
        .map((report) => {
          return {
            [COLUMNS.NAME]: {
              value: report.name,
              route: report.isDashboard
                ? `${this.customReporting.baseRoute}.dashboard`
                : `${this.customReporting.baseRoute}.look`,
              model: report.isDashboard
                ? report.belongsTo('dashboard').id()
                : report.belongsTo('look').id(),
            },
            [COLUMNS.SCHEDULED]: cronUserFriendly(report.crontab),
            [COLUMNS.LAST_RUN_AT]: report.lastRunAt
              ? report.lastRunAt.format("ddd, MMM D, 'YY h:mm A")
              : '',
            [COLUMNS.TYPE]: report.type,
            [COLUMNS.RECIPIENTS]: report.recipients.length,
            [COLUMNS.REPORT]: {
              report,
            },
          };
        })
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sorts() {
    return [
      {
        valuePath: this.sort,
        isAscending: this.direction === 'asc',
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalPages() {
    if (!this.reports) {
      return 0;
    }

    return Math.ceil([...this.reports].length / this.limit);
  }

  @action onUpdateSorts(
    sorts: { isAscending: boolean; valuePath: SortableField }[],
  ): void {
    if (sorts[0]) {
      this.sort = sorts[0].valuePath;
      this.direction = sorts[0].isAscending ? 'asc' : 'desc';
    } else {
      this.sort = DEFAULT_SORT;
      this.direction = DEFAULT_DIRECTION;
    }
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sortReports(a: LookerScheduledPlan, b: LookerScheduledPlan) {
    const { sort, direction } = this;
    let comparison = 0;

    switch (sort) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
      case 'last-run-at':
        comparison = a.lastRunAt?.isAfter(b.lastRunAt) ? 1 : -1;
        break;
      default:
        sort satisfies never;
    }

    return direction === 'desc' ? comparison * -1 : comparison;
  }
}
